import * as React from "react";
import { useState } from "react";
import { useSocialCoachSession, useImpersonation } from "@/utils";
import { Button } from "@/components/ui/button";
import { ShieldAlert, ArrowLeftRight, Undo2, Loader2, GripVertical } from "lucide-react";
import { useDraggable } from "./useDraggable";

export const ImpersonationBanner: React.FC = () => {
  const { isImpersonating, session } = useSocialCoachSession();
  const { revert } = useImpersonation();
  const [isReverting, setIsReverting] = useState(false);

  // Use custom draggable hook for isolated dragging and physics logic
  const { containerRef, bannerRef, position, setPosition, isDragging, resetPosition, dragHandlers } = useDraggable({
    key: "impersonation_banner_position",
  });

  // Minimize state
  const [isMinimized, setIsMinimized] = useState(() => {
    if (typeof window !== "undefined") {
      try {
        const saved = localStorage.getItem("impersonation_banner_minimized");
        return saved === "true";
      } catch (e) {
        console.warn("Failed to load impersonation banner minimize state:", e);
      }
    }
    return false;
  });

  if (!isImpersonating) return null;

  const handleRevert = async () => {
    setIsReverting(true);
    try {
      await revert();
    } catch (e) {
      console.error("Failed to revert impersonation:", e);
      setIsReverting(false);
    }
  };

  const handleOpenSwitch = () => {
    window.dispatchEvent(new CustomEvent("open-global-impersonation-switch"));
  };

  const handleToggleMinimize = () => {
    setIsMinimized(prev => {
      const next = !prev;

      // If we are expanding (next === false), make sure we pull the x coordinate
      // in-bounds to prevent the expanded 360px banner from clipping off-screen!
      if (!next) {
        const padding = 10;
        const maxExpandedX = window.innerWidth / 2 - 180 - padding;
        const nextX = Math.min(maxExpandedX, position.x);
        setPosition({ x: nextX, y: position.y });
        try {
          localStorage.setItem("impersonation_banner_position", JSON.stringify({ x: nextX, y: position.y }));
        } catch (e) {}
      }

      try {
        localStorage.setItem("impersonation_banner_minimized", String(next));
      } catch (e) {
        console.warn("Failed to save minimize state:", e);
      }
      return next;
    });
  };

  const handleGrabberClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.detail === 3) {
      resetPosition();
      setIsMinimized(false);
      try {
        localStorage.setItem("impersonation_banner_minimized", "false");
      } catch (err) {
        console.warn("Failed to save minimize state:", err);
      }
    }
  };

  const isDetached = position.y > 2;

  return (
    <div
      ref={containerRef}
      className="group"
      style={
        {
          "--drag-x": `${position.x}px`,
          "--drag-y": `${position.y}px`,
          "--is-detached": isDetached ? "1" : "0",
        } as React.CSSProperties
      }
    >
      {/* Full-width 2px secure indicator line across the very top of the viewport */}
      <div
        className="secure-top-line fixed top-0 left-0 right-0 h-[2px] bg-primary z-[100] pointer-events-none transition-opacity duration-300 group-hover:shadow-[0_2px_8px_rgba(0,0,0,0.22)]"
        style={{
          opacity: "calc(1 - var(--is-detached, 0))",
        }}
      />

      <div
        ref={bannerRef}
        style={{
          transform: "translate(calc(-180px + var(--drag-x, 0px)), var(--drag-y, 0px))",
          transition: isDetached
            ? "width 300ms cubic-bezier(0.4, 0, 0.2, 1), min-width 300ms cubic-bezier(0.4, 0, 0.2, 1), border-radius 300ms cubic-bezier(0.4, 0, 0.2, 1) 0ms, padding 300ms cubic-bezier(0.4, 0, 0.2, 1), box-shadow 300ms cubic-bezier(0.4, 0, 0.2, 1)"
            : "width 300ms cubic-bezier(0.4, 0, 0.2, 1), min-width 300ms cubic-bezier(0.4, 0, 0.2, 1), border-radius 0ms cubic-bezier(0.4, 0, 0.2, 1), padding 300ms cubic-bezier(0.4, 0, 0.2, 1), box-shadow 300ms cubic-bezier(0.4, 0, 0.2, 1)",
        }}
        className={`fixed top-0 left-1/2 z-[100] bg-primary text-white h-10 shadow-[0_2px_6px_rgba(0,0,0,0.24)] border-primary/20 flex items-center justify-start gap-2.5 backdrop-blur-sm bg-opacity-95 select-none ${
          isMinimized ? "w-[80px] pl-2 pr-3.5" : "w-[360px] pl-1.5 pr-1.5"
        } ${
          isDetached
            ? "rounded-xl border shadow-[0_4px_12px_rgba(0,0,0,0.28)]"
            : "rounded-b-xl border-x border-b group-hover:shadow-[0_4px_10px_rgba(0,0,0,0.35)]"
        }`}
      >
        {/* Concave Corner Fillets - always rendered, opacity & horizontal slide toggled in real-time */}
        <>
          <svg
            className="fillet-svg absolute top-0 right-full h-4 w-4 text-primary pointer-events-none transition-all duration-300"
            style={{
              opacity: "calc(1 - var(--is-detached, 0))",
              transform: "translateX(calc(var(--is-detached, 0) * 16px))",
            }}
            viewBox="0 0 16 16"
            fill="currentColor"
          >
            <path d="M16 16 L16 0 L0 0 A 16 16 0 0 1 16 16 Z" />
          </svg>
          <svg
            className="fillet-svg absolute top-0 left-full h-4 w-4 text-primary pointer-events-none transition-all duration-300"
            style={{
              opacity: "calc(1 - var(--is-detached, 0))",
              transform: "translateX(calc(var(--is-detached, 0) * -16px))",
            }}
            viewBox="0 0 16 16"
            fill="currentColor"
          >
            <path d="M0 16 L0 0 L16 0 A 16 16 0 0 0 0 16 Z" />
          </svg>
        </>

        {/* 1. Draggable grabber container */}
        <div
          {...dragHandlers}
          onClick={handleGrabberClick}
          style={{ touchAction: "none" }}
          className={`text-white/40 hover:text-white/70 transition-colors shrink-0 flex items-center justify-center p-1 rounded hover:bg-white/10 ${
            isDragging ? "cursor-grabbing text-white animate-pulse" : "cursor-grab"
          }`}
          title="Drag to reposition"
        >
          <GripVertical className="h-4 w-4" />
        </div>

        {/* 2. Clickable Shield Alert Icon to minimize/expand */}
        <button
          type="button"
          onClick={handleToggleMinimize}
          className="text-white/90 hover:text-white hover:scale-110 active:scale-95 transition-all cursor-pointer shrink-0 p-1 rounded hover:bg-white/10"
          title={isMinimized ? "Click to expand" : "Click to minimize"}
        >
          <ShieldAlert className="h-4 w-4 shrink-0 text-white/90" />
        </button>

        {/* 3. Viewing Name */}
        <div
          className={`flex items-center ml-0.5 min-w-0 transition-all ${
            isMinimized
              ? "absolute opacity-0 pointer-events-none duration-0"
              : "relative opacity-100 duration-200 delay-150 flex-1"
          }`}
        >
          <span className="text-xs font-semibold flex items-center gap-1 leading-none min-w-0 w-full">
            <span className="opacity-90 shrink-0">Viewing As:</span>
            <span
              className="font-bold cursor-pointer truncate"
              onClick={handleOpenSwitch}
              title={`Click to switch user (${session?.fullName})`}
            >
              {session?.fullName}
            </span>
          </span>
        </div>

        {/* 4. Right-aligned block: Switcher & Revert Actions with a subtle vertical divider */}
        <div
          className={`flex items-center gap-1.5 shrink-0 ml-auto transition-all ${
            isMinimized
              ? "absolute opacity-0 pointer-events-none duration-0"
              : "relative opacity-100 duration-200 delay-150"
          }`}
        >
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7 text-white hover:bg-white/10 hover:text-white p-0"
            onClick={handleOpenSwitch}
            disabled={isReverting}
            title="Switch User"
          >
            <ArrowLeftRight className="h-4 w-4" />
          </Button>

          <div className="w-px h-3.5 bg-white/20" />

          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7 text-white hover:bg-white/10 hover:text-white p-0"
            onClick={handleRevert}
            disabled={isReverting}
            title={isReverting ? "Returning to My Account..." : "Return to My Account"}
          >
            {isReverting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Undo2 className="h-4 w-4" />}
          </Button>
        </div>
      </div>
    </div>
  );
};
